// Centralised API client for Nivo CRM.
// All requests are made against REACT_APP_BACKEND_URL + '/api'.
// Web uses httpOnly cookies -> we always send `credentials: 'include'`.
//
// Phase 9: network errors and 5xx responses now automatically surface a
// French toast ("Connexion perdue. Réessayez.") with a "Réessayer" action
// that re-runs the failed request. Callers can opt-out per call via
// `{ silent: true }` when they render their own error UI (e.g. the login
// flow, some background pollers).
import { toastNetworkError } from './toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Build a URL from a path + optional query params. Empty / null / undefined
// values are stripped so the backend never sees `?statut=`.
function buildUrl(path, params) {
    let url = `${API}${path}`;
    if (!params) return url;
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') usp.append(k, v);
    });
    const s = usp.toString();
    return s ? `${url}?${s}` : url;
}

// Shared fetch wrapper.
// - Serialises JSON bodies.
// - Parses JSON responses (falls back to text for non-JSON).
// - Throws with { status, detail } on non-2xx.
// - Network error (fetch reject) or HTTP 5xx → surfaces the "Connexion perdue"
//   toast with a retry button that re-runs the exact same request. Callers
//   can suppress the toast via { silent: true }.
export async function request(path, opts = {}) {
    const { method = 'GET', body, headers = {}, params, silent = false } = opts;
    const url = buildUrl(path, params);
    const init = {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...headers },
    };
    if (body !== undefined) init.body = JSON.stringify(body);

    let res;
    try {
        res = await fetch(url, init);
    } catch (e) {
        // Real network failure (offline, DNS, CORS at browser level).
        if (!silent) toastNetworkError(() => request(path, opts));
        const err = new Error('Connexion perdue. Réessayez.');
        err.status = 0;
        err.network = true;
        throw err;
    }

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const payload = isJson
        ? await res.json().catch(() => ({}))
        : await res.text().catch(() => '');

    if (!res.ok) {
        // Phase F2.1 — JAMAIS afficher une page HTML (502/504 ingress ou
        // Cloudflare) dans un toast utilisateur. Si la réponse est JSON on
        // conserve `detail`/`message` intact (ex. 4xx FastAPI avec messages
        // métier type « Seul un agent titulaire d'un autre call center peut
        // couvrir. »). Sinon → message générique, détail en console.
        let detail;
        if (isJson && payload && typeof payload === 'object') {
            detail = payload.detail ?? payload.message ?? `Erreur serveur (${res.status})`;
        } else {
            if (payload) {
                console.warn('[api] non-JSON error payload', res.status,
                    String(payload).slice(0, 200));
            }
            detail = `Erreur serveur (${res.status}), réessayez`;
        }
        const err = new Error(typeof detail === 'string' ? detail : 'Erreur');
        err.status = res.status;
        err.detail = detail;
        // 5xx → treat as a transient server failure and offer retry too.
        if (!silent && res.status >= 500) {
            toastNetworkError(() => request(path, opts));
        }
        throw err;
    }
    return payload;
}

export const api = {
    me: () => request('/auth/me', { silent: true }),
    logout: () => request('/auth/logout', { method: 'POST', silent: true }),
    // Background poller — DON'T surface a global toast when the webhook health
    // endpoint blips; the top-bar dot degrades naturally.
    webhookHealth: () => request('/health/webhook', { silent: true }),
};
